import React, { useEffect, useState } from "react";
import styles from "./DocumentUploader.module.css";
import { Row, Col, Accordion } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import DropFileInput from "../../../../../pages/reusable_components/drop_file_component/DropFileComponent";
import {
  deleteDocumentForClient,
  fetchDocumentForClient,
  fetchDocumentTypes,
  uploadDocumentForClient,
} from "./DocumentUploader.service";
import { DocumentUploaderProps, ClientDocument, downloadFile } from "./DocumentUploader.utils";
import { FileInputTypes } from "../../../../../pages/client_onboarding/ClientsGrid/ClientsGrid.utils";
import PdfViewer from "../../../../reusable_components/PdfViewer/PdfViewer";
import SpreadsheetViewer from "../../../../../Reusables/components/SpreadsheetViewer/SpreadsheetViewer";
import ImageViewer from "../../../../../Reusables/components/ImageViewer/ImageViewer";
import TextViewer from "../../../../../Reusables/components/TextViewer/TextViewer";
import WordDocumentViewer from "../../../../../Reusables/components/WordDocumentViewer/WordDocumentViewer";
import { FieldProps } from "../../../../../Reusables/components/InputFieldsController/InputFieldsController.utils";

/**
 * Returns a Bootstrap icon element based on the file type extension.
 */
export const getFileIcon = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "pdf":
      return <Icon.FilePdfFill size={16} color="#D32F2F" />;
    case "xlsx":
    case "xls":
    case "csv":
      return <Icon.FileEarmarkSpreadsheetFill size={16} color="#2E7D32" />;
    case "doc":
    case "docx":
      return <Icon.FileWordFill size={16} color="#1565C0" />;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "bmp":
    case "tiff":
    case "webp":
    case "svg":
      return <Icon.FileImageFill size={16} color="#6A1B9A" />;
    default:
      return <Icon.FileEarmarkFill size={16} color="#616161" />;
  }
};

/**
 * DocumentUploader
 *
 * Accordion-based component for uploading and managing client documents.
 * Combines UI from original ClientDocuments with upload logic from ClientFilesUploader.
 */
const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  clientMasterId,
  fields,
  disabled = false,
  onValidationChange,
  formFieldsByTab
}) => {
  const [uploadProgressByDocType, setUploadProgressByDocType] = useState<{
    [referenceCode: string]: boolean;
  }>({});
  const [existingDocuments, setExistingDocuments] = useState<{
    [referenceCode: string]: ClientDocument[];
  }>({});
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string } | null>(null);
  const [fileTypes, setFileTypes] = useState<FileInputTypes[]>([]);
  const [filteredFileTypes, setFilteredFileTypes] = useState<FileInputTypes[]>([]);
  const [apiProgress, setApiProgress] = useState<boolean>(false);

  useEffect(() => {
  if (!formFieldsByTab) return;

  fetchDocumentTypes((types) => {
    setFileTypes(types);
    console.log("Fetched document types:", types);
    console.log("Fields prop:", fields);
    const allFormFields = Object.values(formFieldsByTab).flat() as FieldProps[];
    console.log("All form fields:", allFormFields);

    const isFieldVisible = (formField: FieldProps): boolean => {
      if (formField.show === false) return false;

      const dependsOn = formField.dependsOn?.trim();
      if (!dependsOn) return true;

      const parentField = allFormFields.find(
        (f) => f.allfields_code?.trim() === dependsOn
      );
      if (!parentField) return true;

      const parentKey = parentField.allfields_key?.trim();
      if (!parentKey) return true;

      const parentValue = fields?.[parentKey];

      const visibilityValues: any[] = formField.dependsOnVisibilityValues?.length
        ? formField.dependsOnVisibilityValues
        : [formField.dependsOnVisibilityValue];

      return visibilityValues.some(
        (v) => String(v).toLowerCase() === String(parentValue).toLowerCase()
      );
    };

    const filtered = types.filter((type: FileInputTypes) => {
      const fieldCode = type.FieldCode?.trim();

      const formField = allFormFields.find(
        (f) => f.allfields_code?.trim().toUpperCase() === fieldCode.toUpperCase()
      );

      if (!formField || !isFieldVisible(formField)) return false;

      const lookupKey = formField.allfields_key?.trim();
      if (!lookupKey) return false;

      const savedValue = fields?.[lookupKey];

      return savedValue === true || savedValue === "true";
    });
    console.log("FILTERED: ", filtered)
    setFilteredFileTypes(filtered);
  }, setApiProgress, false);
}, [formFieldsByTab, fields]);

  /** Compute and report document validation status */
  useEffect(() => {
    if (!onValidationChange) return;

    // All filtered types are "required" because their checkbox was checked
    const missing = filteredFileTypes
      .filter(type => (existingDocuments[type.FieldCode]?.length ?? 0) === 0)
      .map(type => type.FieldName);

    onValidationChange({
      isValid: missing.length === 0,
      missing
    });
  }, [filteredFileTypes, existingDocuments, onValidationChange]);

  /** Compute and report document validation status */
  useEffect(() => {
    if (!onValidationChange) return;

    // All filtered types are "required" because their checkbox was checked
    const missing = filteredFileTypes
      .filter(type => (existingDocuments[type.FieldCode]?.length ?? 0) === 0)
      .map(type => type.FieldName);

    onValidationChange({
      isValid: missing.length === 0,
      missing
    });
  }, [filteredFileTypes, existingDocuments, onValidationChange]);

  /** Load existing documents per reference code. */
  const fetchExistingDocument = async (
    firmReferenceId: number,
    referenceCode: string
  ) => {
    const data = await fetchDocumentForClient(
      clientMasterId,
      () => { },
      referenceCode,
      firmReferenceId
    );

    if (data) {
      setExistingDocuments((prev) => {
        const updated = { ...prev, [referenceCode]: data };

        // Show first document automatically if none currently previewed
        if (!previewFile && data.length > 0) {
          const firstFile = data[0];
          setPreviewFile({
            url: firstFile.BlobUrl,
            name: firstFile.FileName,
          });
        }

        return updated;
      });
    }
  };

  useEffect(() => {
    if (filteredFileTypes.length > 0 && clientMasterId) {
      filteredFileTypes.forEach((input) => {
        fetchExistingDocument(input.FirmReferenceDataId, input.FieldCode);
      });
    } else {
      setPreviewFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFileTypes, clientMasterId]);

  /** Upload documents per type */
  const handleFileChange = async (
    files: File[],
    event: React.ChangeEvent<HTMLInputElement>,
    firmReferenceId: number,
    referenceCode: string
  ) => {
    if (!files.length) return;

    setUploadProgressByDocType((prev) => ({
      ...prev,
      [referenceCode]: true,
    }));

    const documentMetaData = files.map((file) => ({
      DocumentTypeCode: referenceCode,
      FileName: file.name,
      Status: "Completed",
    }));

    const response = await uploadDocumentForClient(
      clientMasterId,
      documentMetaData,
      files,
      (progress) => {
        setUploadProgressByDocType((prev) => ({
          ...prev,
          [referenceCode]: progress,
        }));
      }
    );

    if (response.success && response.data[0].Documents?.length > 0) {
      const uploadedMetadata: ClientDocument[] = response.data[0].Documents.map((doc: any) => ({
        ClientDocumentId: doc.ClientDocumentId,
        FirmId: response.data[0].FirmId,
        ClientMasterId: response.data[0].ClientMasterId,
        ClientName: response.data[0].ClientName || "",
        FileName: doc.FileName,
        BlobUrl: doc.BlobUrl,
        DocumentTypeCode: doc.DocumentTypeCode,
        DocumentTypeName: "",
        Status: "Completed",
        Active: doc.Active,
        CreatedBy: "",
        CreatedDate: new Date().toISOString(),
        UpdatedBy: "",
        UpdatedDate: new Date().toISOString(),
      }));

      setExistingDocuments((prev) => {
        const updated = {
          ...prev,
          [referenceCode]: [...(prev[referenceCode] || []), ...uploadedMetadata],
        };

        // Immediately show the newly uploaded file
        const latestFile = uploadedMetadata[uploadedMetadata.length - 1];
        setPreviewFile({
          url: latestFile.BlobUrl,
          name: latestFile.FileName,
        });

        return updated;
      });
    }

    // Reset upload state
    setUploadProgressByDocType((prev) => ({
      ...prev,
      [referenceCode]: false,
    }));
  };

  /** Delete documents by ID */
  const handleDeleteDocument = async (
    file: ClientDocument,
    referenceCode: string,
    clientDocumentId: number
  ) => {
    const confirmed = window.confirm(`Are you sure you want to delete ${file.FileName}?`);
    if (!confirmed) return;

    const success = await deleteDocumentForClient(clientDocumentId);
    if (success) {
      setExistingDocuments((prev) => ({
        ...prev,
        [referenceCode]: prev[referenceCode].filter(
          (doc) => doc.ClientDocumentId !== clientDocumentId
        ),
      }));

      // Clear preview if deleted file is being viewed
      if (previewFile?.url === file.BlobUrl) {
        setPreviewFile(null);
      }
    }
  };

  return (
    <>
      <Row>
        <Col md={4}>
          <div className={styles.filesViewerWrapper}>
            {/* Default open first accordion item */}
            <Accordion className={styles.uploadFilesAccordion} defaultActiveKey="0">
              {filteredFileTypes.map((input, index) => (
                <Accordion.Item eventKey={String(index)} key={input.FirmReferenceDataId}>
                  <Accordion.Header className={styles.uploadFilesAccordianHeader}>
                    <div className={styles.uploadFilesAccordianHeaderContent}>
                      <div>
                        {input.FieldName}
                        <span className={styles.requiredAsterisk}>*</span>
                      </div>
                      <div
                        className={
                          styles.existingFileInfo +
                          " " +
                          (existingDocuments[input.FieldCode]?.length > 0
                            ? styles.availableFiles
                            : "")
                        }
                      >
                        {existingDocuments[input.FieldCode]?.length || 0}
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body className={styles.uploadFilesAccordianBody}>
                    <div className={styles.dropFileWrapper}>
                      <DropFileInput
                        onFileChange={(files: File[], event: React.ChangeEvent<HTMLInputElement>) =>
                          handleFileChange(
                            files,
                            event,
                            input.FirmReferenceDataId,
                            input.FieldCode
                          )
                        }
                        uploading={uploadProgressByDocType[input.FieldCode]}
                        message={`Upload your ${input.ReferenceDataValue} document`}
                        disabled={disabled}
                      />
                    </div>

                    {existingDocuments[input.FieldCode]?.length > 0 && (
                      <div className={styles.uploadedFileList}>
                        <div className={styles.uploadedFileTitle}>Uploaded Files:</div>
                        {existingDocuments[input.FieldCode].map((file, idx) => (
                          <div key={idx} className={styles.uploadedFileRow}>
                            <div className={styles.uploadedFileName}>
                              {getFileIcon(file.FileName)}
                              <span>{file.FileName}</span>
                            </div>
                            <div className={styles.uploadedFileActions}>
                              <Icon.Eye
                                className={styles.viewIcon}
                                title="View Document"
                                onClick={() =>
                                  setPreviewFile({
                                    url: file.BlobUrl,
                                    name: file.FileName,
                                  })
                                }
                              />
                              <Icon.Download
                                className={styles.downloadIcon}
                                title="Download Document"
                                onClick={() => downloadFile(file.BlobUrl, file.FileName)}
                              />
                              {!disabled && <Icon.Trash
                                className={styles.deleteIcon}
                                title="Delete Document"
                                onClick={() =>
                                  handleDeleteDocument(file, input.FieldCode, file.ClientDocumentId)
                                }
                              />}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>
        </Col>

        <Col md={8}>
          <div className={styles.viewerWrapper}>
            {/\.xlsx$|\.xls$|\.csv$/i.test(previewFile?.url || "") ? (
              <SpreadsheetViewer preview={previewFile} />
            ) : /\.docx$|\.doc$/i.test(previewFile?.url || "") ? (
              <WordDocumentViewer preview={previewFile!!} />
            ) : /\.(jpg|jpeg|png|gif|bmp|tiff|webp|svg)$/i.test(previewFile?.url || "") ? (
              <ImageViewer preview={previewFile} />
            ) : /\.(txt|log|json|xml|yaml|yml|md|rtf)$/i.test(previewFile?.url || "") ? (
              <TextViewer preview={previewFile} />
            ) : (
              <PdfViewer preview={previewFile} />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default DocumentUploader;
